import React, { useCallback, useEffect, useMemo, useState, createElement, Component } from 'react';
import { useParams, Link, useNavigate } from 'react-router-dom';
import { AdminLayout } from '../../components/layout/AdminLayout';
import { useRestaurantBySlug } from '../../lib/hooks';
import { useTenant } from '../../lib/tenant-context';
import { useTableManagement, useTableMetrics, useTableOrderItems, closeTableAccount } from '../../lib/table-hooks';
import { Card, CardContent } from '../../components/ui/Card';
import { Skeleton } from '../../components/ui/Skeleton';
import { Badge } from '../../components/ui/Badge';
import { Modal } from '../../components/ui/Modal';
import { Button } from '../../components/ui/Button';
import { PayTableModal } from '../../components/admin/PayTableModal';
import { CabanaAddItemsModal } from '../../components/admin/CabanaAddItemsModal';
import { CabanaModifyOrderModal } from '../../components/admin/CabanaModifyOrderModal';
import { ErrorBoundary } from '../../components/ui/ErrorBoundary';
import { useAdminOrders } from '../../lib/admin-hooks';
import { cn, formatCurrency } from '../../lib/utils';
import { TableWithOrder, TableStatus, OrderChannel } from '../../lib/types';
import { Armchair, QrCode, User, Clock, ShoppingBag, Hash, Users, Receipt, Store, Smartphone, MonitorSmartphone, CreditCard, Banknote, X, Loader2, ChefHat, Plus, Pencil, Printer } from 'lucide-react';
import { supabase } from '../../lib/supabase';
// RanchoActionMenu lives at the bottom of this file (function declaration).
// No external import — keeps everything local and avoids dead-import warnings.
interface Toast {
  id: number;
  type: 'success' | 'error';
  message: string;
}
export function TableManagementPage() {
  const {
    slug
  } = useParams<{
    slug: string;
  }>();
  const navigate = useNavigate();
  const {
    restaurant,
    loading: restLoading
  } = useRestaurantBySlug(slug);
  const tenantCtx = useTenant();
  const {
    tables,
    loading: tablesLoading,
    refetch: refetchTables
  } = useTableManagement(restaurant?.id);
  const {
    metrics,
    loading: metricsLoading
  } = useTableMetrics(restaurant?.id);
  const [selectedTable, setSelectedTable] = useState<TableWithOrder | null>(null);
  const [selectedPayment, setSelectedPayment] = useState<'cash' | 'card' | 'sinpe'>('cash');
  const [isClosing, setIsClosing] = useState(false);
  const [toasts, setToasts] = useState<Toast[]>([]);
  const [showPayModal, setShowPayModal] = useState(false);
  const [showAddItemsModal, setShowAddItemsModal] = useState(false);
  const [showModifyModal, setShowModifyModal] = useState(false);
  // Rancho-only: simple action menu state inside the table modal.
  // 'main'  → primary options (Editar / Cobrar)
  // 'edit'  → sub-options under "Editar / Modificar orden"
  const [ranchoMenuMode, setRanchoMenuMode] = useState<'main' | 'edit'>('main');
  // Fetch items only when a table is selected
  const {
    items: orderItems,
    loading: itemsLoading,
    refetch: refetchItems
  } = useTableOrderItems(selectedTable?.id, restaurant?.id);
  // ─── La Cabaña Parrillera scoping ─────────────────────────────────
  const isCabanaParrillera = String(restaurant?.id) === '24' || restaurant?.slug === 'la-cabana-parrillera' || restaurant?.slug === 'cabana-parrillera';
  // ─── Rancho San Francisco scoping ─────────────────────────────────
  const isRanchoSanFrancisco = String(restaurant?.id) === '28' || restaurant?.slug === 'rancho-san-francisco';
  // ─── NatuCafé Coffee Shop scoping ─────────────────────────────────
  // NatuCafé (id 19) reutiliza el mismo flujo de Mesas que Rancho:
  // grilla limpia con números grandes, modal con Tomar orden / Editar
  // / Cobrar y Facturar, y los mismos modales de Adicionar/Modificar
  // productos. Esto le da a NatuCafé un POS de salón completo, sin
  // tocar nada del marketplace ni de otros restaurantes.
  const isNatuCafe = String(restaurant?.id) === '19' || slug === 'natucafe-coffee-shop'; // Emilius Pizza (id 27) — usa el mismo flujo estilo Rancho/NatuCafé
  // (grilla limpia, Tipo de Orden selector, modal con Editar/Cobrar).
  // Mantiene todas sus estaciones activas (la diferencia con NatuCafé
  // es que Emilius sí tiene Pizzería). NatuCafé NO se ve afectado.
  const isEmilius = String(restaurant?.id) === '27' || restaurant?.slug === 'emilius-pizza';
  // Demo Argentina (id 29) — mismo flujo de Mesas estilo Rancho/Emilius:
  // grilla limpia, modal con Tomar orden / Editar / Cobrar y soporte de
  // adicionar/modificar productos. NatuCafé NO se ve afectado.
  const isDemoArgentina = String(restaurant?.id) === '29' || restaurant?.slug === 'demo-argentina';
  // Conveniencia: cualquier restaurante que use el flujo estilo Rancho
  // (Rancho + NatuCafé + Emilius + Demo Argentina) — UI limpia,
  // RanchoActionMenu en el modal, sub-menú de Editar, y soporte para
  // adicionar/modificar productos.
  const usesRanchoStyleTables = isRanchoSanFrancisco || isNatuCafe || isEmilius || isDemoArgentina;
  // Cabaña + Rancho + NatuCafé comparten el mismo flujo de
  // "modify/cancel" — solo enganchamos cancelOrder cuando hace falta.
  const {
    cancelOrder,
    refetchOrders: refetchAdminOrders
  } = useAdminOrders(isCabanaParrillera || usesRanchoStyleTables ? restaurant?.id ? String(restaurant.id) : undefined : undefined);
  const addToast = useCallback((type: 'success' | 'error', message: string) => {
    const id = Date.now();
    setToasts(prev => [...prev, {
      id,
      type,
      message
    }]);
    setTimeout(() => {
      setToasts(prev => prev.filter(t => t.id !== id));
    }, 3500);
  }, []);
  const removeToast = useCallback((id: number) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);
  // ─── Rancho San Francisco (id 28) — Print desde Mesas ─────────────
  // Inserta una fila pending en order_print_jobs con un trigger único
  // (manual_<timestamp>) para que el bridge Python imprima el ticket
  // de la orden abierta en la mesa seleccionada. Fetchea los items con
  // sus options primero para que el ticket contenga el detalle real.
  // Solo aplica a Rancho — cero impacto en NatuCafé y los demás.
  const [printingMesa, setPrintingMesa] = useState(false);
  const handleImprimirMesa = useCallback(async () => {
    if (!isRanchoSanFrancisco) return;
    if (!selectedTable?.current_order) return;
    const order = selectedTable.current_order as any;
    setPrintingMesa(true);
    try {
      // 1) Fetch items + options para que el ticket tenga el detalle real.
      //
      // Rancho — el bridge Python imprime `item_name_snapshot` literalmente.
      // Para garantizar que la comanda muestre las opciones seleccionadas
      // (ej. "Casado · Chicharrón" en vez de solo "Casado") enriquecemos
      // el nombre con las opciones inline. También marcamos los ítems de
      // "Arma tu plato" (menu_item_id NULL) con prefijo "[Arma] " para
      // que NO se mezclen visualmente con platillos normales como
      // "Kilo de chicharrones" en la misma comanda.
      let items: any[] = [];
      try {
        const {
          data: rows
        } = await supabase.from('order_items').select('*').eq('order_id', order.id);
        items = rows || [];
        if (items.length > 0) {
          const itemIds = items.map((it: any) => it.id);
          const {
            data: opts
          } = await supabase.from('order_item_options').select('*').in('order_item_id', itemIds);
          const byItem: Record<string, any[]> = {};
          (opts || []).forEach((o: any) => {
            const k = String(o.order_item_id);
            if (!byItem[k]) byItem[k] = [];
            byItem[k].push(o);
          });
          items = items.map((it: any) => {
            const itemOptions = byItem[String(it.id)] || [];
            const baseName = String(it.item_name_snapshot || '').trim();
            const optsSummary = itemOptions.map((o: any) => String(o.option_name_snapshot || o.option_name || '').trim()).filter((s: string) => s.length > 0).join(' · ');
            const isArma = it.menu_item_id == null;
            const armaPrefix = isArma ? '[Arma] ' : '';
            const enrichedName = optsSummary ? `${armaPrefix}${baseName} · ${optsSummary}` : `${armaPrefix}${baseName}`;
            return {
              ...it,
              options: itemOptions,
              is_arma_tu_plato: isArma,
              original_item_name: baseName,
              item_name_snapshot: enrichedName
            };
          });
        }
      } catch (e) {
        console.warn('[RANCHO_PRINT][mesa] items/options fetch non-blocking:', e);
      }
      const triggerValue = `manual_${Date.now()}`;
      // order_print_jobs.order_id es UUID — usamos tracking_token (UUID),
      // mismo patrón que WaiterPOSPage. Si la orden de la mesa no trae
      // tracking_token directamente, hacemos un fetch puntual a orders
      // para obtenerlo (la vista de tables a veces no lo incluye).
      let orderRefUuid: string | null = (order as any).tracking_token ?? null;
      if (!orderRefUuid && order.id) {
        try {
          const {
            data: row
          } = await supabase.from('orders').select('tracking_token').eq('id', order.id).maybeSingle();
          orderRefUuid = (row as any)?.tracking_token ?? null;
        } catch (e) {
          console.warn('[RANCHO_PRINT][mesa] tracking_token fetch failed (non-blocking):', e);
        }
      }
      // Último fallback: usar order.id directamente (es UUID también).
      if (!orderRefUuid) {
        orderRefUuid = order.id ?? null;
      }
      console.log('[RANCHO_PRINT][mesa] Inserting print job — order.id:', order.id, 'tracking_token:', (order as any).tracking_token, 'using ref:', orderRefUuid, 'trigger:', triggerValue);
      if (!orderRefUuid) {
        console.error('[RANCHO_PRINT][mesa] No tracking_token / id available on order', order);
        addToast('error', 'Orden sin identificador (no se puede imprimir)');
        return;
      }
      const {
        error
      } = await supabase.from('order_print_jobs').insert({
        restaurant_id: 28,
        order_id: orderRefUuid,
        order_number: order.order_number ?? null,
        status: 'pending',
        payload: {
          order,
          order_internal_id: order.id ?? null,
          order_number: order.order_number ?? null,
          created_at: order.created_at ?? null,
          customer_name: `Mesa ${selectedTable.table_number}`,
          customer_phone: null,
          delivery_type: 'dine_in',
          payment_method: order.payment_method ?? 'cash',
          table_number: selectedTable.table_number,
          subtotal: order.subtotal ?? 0,
          delivery_fee: 0,
          total: order.total ?? 0,
          notes: order.notes ?? null,
          items,
          trigger: triggerValue
        }
      });
      if (error) {
        console.error('[RANCHO_PRINT][mesa] insert error — full object:', JSON.stringify(error, Object.getOwnPropertyNames(error)));
        addToast('error', `No se pudo imprimir: ${error.message || 'error desconocido'}`);
      } else {
        console.log('[RANCHO_PRINT][mesa] ✅ print job inserted');
        addToast('success', '🖨 Enviado a la impresora');
      }
    } catch (e: any) {
      console.error('[RANCHO_PRINT][mesa] exception:', e);
      addToast('error', e?.message || 'Error al enviar a la impresora');
    } finally {
      setPrintingMesa(false);
    }
  }, [isRanchoSanFrancisco, selectedTable, addToast]);
  const handleCloseAccount = async () => {
    if (!restaurant || !selectedTable || !selectedTable.current_order) return;
    if (!window.confirm(`¿Estás seguro de cerrar la cuenta de la ${selectedTable.table_name || selectedTable.table_number}?`)) {
      return;
    }
    setIsClosing(true);
    try {
      const result = await closeTableAccount(restaurant.id, selectedTable.id, selectedPayment, tenantCtx?.userId || null);
      if (result.success) {
        addToast('success', 'Cuenta cerrada correctamente');
        setSelectedTable(null);
        refetchTables();
      } else {
        addToast('error', result.error || 'Error al cerrar la cuenta');
      }
    } catch (error: any) {
      addToast('error', error.message || 'Error inesperado');
    } finally {
      setIsClosing(false);
    }
  };
  // ─── Keep selectedTable in sync with freshly refetched tables ──────
  // When the user adds/modifies products, refetchTables() replaces every
  // row in `tables` with a brand-new object. The `selectedTable` state
  // would otherwise keep a stale reference (old total, old item_count,
  // possibly an old current_order that no longer exists), and rendering
  // RanchoActionMenu against that stale object can crash the page.
  //
  // We re-resolve selectedTable by id on every tables change. If the
  // table is gone (closed/removed), we clear the modal cleanly.
  useEffect(() => {
    if (!selectedTable) return;
    const fresh = tables.find(t => t.id === selectedTable.id);
    if (!fresh) {
      // Table no longer in the list — close the modal safely.
      setSelectedTable(null);
      setRanchoMenuMode('main');
      return;
    }
    // Only update if the reference actually changed (avoid render loop).
    if (fresh !== selectedTable) {
      setSelectedTable(fresh);
    }
  }, [tables, selectedTable]);
  if (restLoading || !restaurant) {
    return <AdminLayout restaurant={restaurant} role={tenantCtx?.role} onSignOut={tenantCtx?.signOut} data-id="element-5710">
        <div className="min-h-screen bg-[#0B0B0B] flex items-center justify-center p-6" data-id="element-5711">
          <div className="w-16 h-16 rounded-full bg-[#141414] animate-pulse" data-id="element-5712" />
        </div>
      </AdminLayout>;
  }
  const getStatusColor = (status: TableStatus) => {
    switch (status) {
      case 'free':
        return 'bg-emerald-500/10 text-emerald-400 border-emerald-500/20';
      case 'occupied':
        return 'bg-amber-500/10 text-amber-400 border-amber-500/20';
      case 'ordering':
        return 'bg-blue-500/10 text-blue-400 border-blue-500/20';
      case 'waiting_payment':
        return 'bg-purple-500/10 text-purple-400 border-purple-500/20';
      case 'closed':
        return 'bg-gray-500/10 text-gray-400 border-gray-500/20';
      default:
        return 'bg-gray-500/10 text-gray-400 border-gray-500/20';
    }
  };
  const getStatusLabel = (status: TableStatus) => {
    switch (status) {
      case 'free':
        return 'Disponible';
      case 'occupied':
        return 'Ocupada';
      case 'ordering':
        return 'Ordenando';
      case 'waiting_payment':
        return 'Por Pagar';
      case 'closed':
        return 'Cerrada';
      default:
        return 'Desconocido';
    }
  };
  const getSourceInfo = (channel: OrderChannel | null) => {
    switch (channel) {
      case 'qr':
        return {
          label: 'QR',
          icon: QrCode,
          color: 'bg-emerald-500/20 text-emerald-400'
        };
      case 'waiter':
        return {
          label: 'Mesero',
          icon: User,
          color: 'bg-blue-500/20 text-blue-400'
        };
      case 'cashier':
        return {
          label: 'Caja',
          icon: Store,
          color: 'bg-amber-500/20 text-amber-400'
        };
      case 'web':
        return {
          label: 'Web',
          icon: MonitorSmartphone,
          color: 'bg-gray-500/20 text-gray-400'
        };
      default:
        return {
          label: 'Desconocido',
          icon: Smartphone,
          color: 'bg-gray-500/20 text-gray-400'
        };
    }
  };
  const getItemStatusBadge = (status: string) => {
    switch (status) {
      case 'pending':
        return 'bg-gray-100 text-gray-600';
      case 'preparing':
        return 'bg-amber-100 text-amber-700';
      case 'ready':
        return 'bg-emerald-100 text-emerald-700';
      case 'served':
        return 'bg-blue-100 text-blue-700';
      default:
        return 'bg-gray-100 text-gray-600';
    }
  };
  const getItemStatusLabel = (status: string) => {
    switch (status) {
      case 'pending':
        return 'Pendiente';
      case 'preparing':
        return 'Preparando';
      case 'ready':
        return 'Listo';
      case 'served':
        return 'Servido';
      default:
        return status;
    }
  };
  return <ErrorBoundary label="TableManagementPage" onReset={() => {
    // On retry, close any open modal and refetch — gives us a clean slate.
    setSelectedTable(null);
    setRanchoMenuMode('main');
    setShowPayModal(false);
    setShowAddItemsModal(false);
    setShowModifyModal(false);
    refetchTables();
  }} data-id="element-5713">
      <AdminLayout restaurant={restaurant} role={tenantCtx?.role} onSignOut={tenantCtx?.signOut} data-id="element-5714">
        {usesRanchoStyleTables ?
      // ─── Rancho San Francisco + NatuCafé — Clean big-number mesa grid ─
      // Diseñado para señores de 70 años: tipografía grande, alto contraste,
      // un solo gesto (tap en la mesa → modal con todo lo que pueden hacer).
      <div className="min-h-screen bg-[#F5F1EB] p-4 sm:p-8 font-sans" data-id="element-5715">
            <div className="max-w-7xl mx-auto" data-id="element-5716">
              {/* Header */}
              <div className="flex flex-col sm:flex-row justify-between items-start sm:items-end gap-4 mb-8" data-id="element-5717">
                <div data-id="element-5718">
                  <h1 className="text-3xl sm:text-4xl font-black text-[#0B0807] tracking-tight" data-id="element-5719">
                    Selecciona una mesa
                  </h1>
                  <p className="text-[#6B5E4F] text-base sm:text-lg mt-1 font-medium" data-id="element-5720">
                    Toca una mesa para ver, cobrar o modificar la orden
                  </p>
                </div>
                <Link to={`/admin/${restaurant.slug}/qr`} className="flex items-center gap-2 px-5 py-3 bg-white hover:bg-gray-50 text-[#0B0807] font-bold text-base rounded-2xl border-2 border-[#E5DCC9] shadow-sm transition-all" data-id="element-5721">
                  <QrCode className="w-5 h-5" data-id="element-5722" />
                  Códigos QR
                </Link>
              </div>

              {/* ─── Tipo de Orden selector ──────────────────────────────
               Three cards above the tables grid: Mesa (stays on this
               page, current behavior), Para llevar y Express (deep-link
               al WaiterPOS con mode preseleccionado vía ?mode=).
               Habilitado para NatuCafé y Rancho San Francisco — el
               resto de restaurantes mantiene su flujo mesa-only.
               */}
              {(isNatuCafe || isRanchoSanFrancisco || isEmilius) && <div className="mb-8" data-id="element-5723">
                  <h2 className="text-xs font-bold text-[#6B5E4F] uppercase tracking-[0.18em] mb-3" data-id="element-5724">
                    Tipo de orden
                  </h2>
                  <div className="grid grid-cols-3 gap-3 max-w-3xl" data-id="element-5725">
                    {/* Mesa — highlights since this is the current view */}
                    <div className="flex flex-col items-center justify-center gap-2 py-5 px-3 rounded-2xl border-2 font-bold transition-all bg-[#0B0807] text-white border-[#0B0807] shadow-sm" aria-current="page" data-id="element-5726">
                      <span className="text-3xl leading-none" data-id="element-5727">🍽️</span>
                      <span className="text-sm" data-id="element-5728">Mesa</span>
                    </div>
                    {/* Para llevar */}
                    <button type="button" onClick={() => navigate(`/admin/${restaurant.slug}/waiter?mode=pickup`)} className="flex flex-col items-center justify-center gap-2 py-5 px-3 rounded-2xl border-2 font-bold transition-all bg-white text-[#0B0807] border-[#E5DCC9] hover:border-[#C6A969] hover:bg-[#FBF7EE] shadow-sm" data-id="element-5729">
                      <span className="text-3xl leading-none" data-id="element-5730">🥡</span>
                      <span className="text-sm" data-id="element-5731">Para llevar</span>
                    </button>
                    {/* Express */}
                    <button type="button" onClick={() => navigate(`/admin/${restaurant.slug}/waiter?mode=delivery`)} className="flex flex-col items-center justify-center gap-2 py-5 px-3 rounded-2xl border-2 font-bold transition-all bg-white text-[#0B0807] border-[#E5DCC9] hover:border-[#C6A969] hover:bg-[#FBF7EE] shadow-sm" data-id="element-5732">
                      <span className="text-3xl leading-none" data-id="element-5733">🛵</span>
                      <span className="text-sm" data-id="element-5734">Express</span>
                    </button>
                  </div>
                </div>}

              {tablesLoading ? <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 sm:gap-5" data-id="element-5735">
                  {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10, 11, 12, 13, 14, 15].map(i => <div key={i} className="aspect-[4/5] rounded-3xl bg-white border-2 border-[#E5DCC9] animate-pulse" data-id="element-5736" />)}
                </div> : tables.length === 0 ? <div className="py-24 flex flex-col items-center justify-center text-[#6B5E4F] bg-white rounded-3xl border-2 border-dashed border-[#E5DCC9]" data-id="element-5737">
                  <Armchair className="w-20 h-20 text-[#C6A969]/40 mb-4" data-id="element-5738" />
                  <h3 className="text-2xl font-black text-[#0B0807] mb-1" data-id="element-5739">
                    No hay mesas configuradas
                  </h3>
                  <p className="text-base" data-id="element-5740">
                    {isNatuCafe ? 'Corre el script ADD_NATUCAFE_TABLES_15_PLUS_BARRA.sql en Supabase para crear las mesas y la barra.' : 'Corre el script SETUP_RANCHO_TABLES.sql en Supabase para crear las 15 mesas.'}
                  </p>
                </div> : <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 sm:gap-5" data-id="element-5741">
                  {[...tables].sort((a, b) => {
              const na = parseInt(String(a.table_number), 10);
              const nb = parseInt(String(b.table_number), 10);
              if (Number.isNaN(na) || Number.isNaN(nb)) {
                return String(a.table_number).localeCompare(String(b.table_number));
              }
              return na - nb;
            }).map(table => {
              const isOccupied = table.status !== 'free';
              const tableNum = String(table.table_number || '').padStart(2, '0');
              return <button key={table.id} onClick={() => {
                setRanchoMenuMode('main');
                setSelectedTable(table);
              }} className={cn('aspect-[4/5] bg-white rounded-3xl border-2 flex flex-col items-center justify-center p-4 transition-all shadow-sm hover:shadow-md hover:-translate-y-0.5 focus:outline-none focus:ring-4 focus:ring-[#C6A969]/40', isOccupied ? 'border-[#F4D35E] bg-[#FFFBEA]' : 'border-[#E5DCC9] hover:border-[#C6A969]')} data-id="element-5742">
                          <span className="text-5xl sm:text-6xl font-black text-[#0B0807] leading-none tabular-nums" data-id="element-5743">
                            {tableNum}
                          </span>
                          <span className="text-base font-bold text-[#6B5E4F] mt-3" data-id="element-5744">
                            {table.table_name || `Mesa ${table.table_number}`}
                          </span>
                          <span className={cn('mt-3 px-4 py-1 rounded-full text-sm font-black', isOccupied ? 'bg-amber-100 text-amber-800' : 'bg-emerald-100 text-emerald-800')} data-id="element-5745">
                            {isOccupied ? 'Ocupada' : 'Libre'}
                          </span>
                          {isOccupied && table.current_order && <span className="mt-2 text-sm font-black text-[#0B0807] tabular-nums" data-id="element-5746">
                              {formatCurrency(table.current_order.total)}
                            </span>}
                        </button>;
            })}
                </div>}
            </div>
          </div> : <div className="min-h-screen bg-[#0B0B0B] p-4 sm:p-6 text-white font-sans relative" data-id="element-5747">
            {/* Header */}
            <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4 mb-6" data-id="element-5748">
              <div data-id="element-5749">
                <h1 className="text-2xl sm:text-3xl font-black text-white tracking-tight flex items-center gap-3" data-id="element-5750">
                  Gestión de Mesas
                  {!metricsLoading && <span className="bg-white/10 text-white/70 px-3 py-1 rounded-full text-sm font-bold tabular-nums" data-id="element-5751">
                      {metrics.activeTables} / {metrics.totalTables} activas
                    </span>}
                </h1>
                <p className="text-white/50 text-sm mt-1" data-id="element-5752">
                  Vista general de la operación en salón y autoatención
                </p>
              </div>
              <Link to={`/admin/${restaurant.slug}/qr`} className="flex items-center gap-2 px-4 py-2.5 bg-white/10 hover:bg-white/15 text-white font-bold text-sm rounded-xl border border-white/10 transition-all" data-id="element-5753">
                <QrCode className="w-4 h-4" data-id="element-5754" />
                Códigos QR
              </Link>
            </div>

            {/* Metrics Row */}
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3 sm:gap-4 mb-8" data-id="element-5755">
              {metricsLoading ? [1, 2, 3, 4].map(i => <div key={i} className="h-28 rounded-2xl bg-[#141414] animate-pulse border border-white/5" data-id="element-5756" />) : <>
                  <div className="bg-[#141414] rounded-2xl p-4 border-l-4 border-y border-r border-white/5 border-l-emerald-500 shadow-lg" data-id="element-5757">
                    <div className="flex items-center justify-between mb-2" data-id="element-5758">
                      <div className="w-8 h-8 rounded-full bg-emerald-500/20 flex items-center justify-center" data-id="element-5759">
                        <QrCode className="w-4 h-4 text-emerald-400" data-id="element-5760" />
                      </div>
                      <span className="text-2xl font-black tabular-nums" data-id="element-5761">
                        {metrics.qrOrdersToday}
                      </span>
                    </div>
                    <div className="flex items-center justify-between" data-id="element-5762">
                      <p className="text-sm font-bold text-white/50" data-id="element-5763">
                        Pedidos QR
                      </p>
                      <span className="text-xs font-bold text-emerald-400 bg-emerald-500/10 px-2 py-0.5 rounded-md" data-id="element-5764">
                        {metrics.selfServicePercent}% autoatención
                      </span>
                    </div>
                  </div>

                  <div className="bg-[#141414] rounded-2xl p-4 border-l-4 border-y border-r border-white/5 border-l-blue-500 shadow-lg" data-id="element-5765">
                    <div className="flex items-center justify-between mb-2" data-id="element-5766">
                      <div className="w-8 h-8 rounded-full bg-blue-500/20 flex items-center justify-center" data-id="element-5767">
                        <User className="w-4 h-4 text-blue-400" data-id="element-5768" />
                      </div>
                      <span className="text-2xl font-black tabular-nums" data-id="element-5769">
                        {metrics.waiterOrdersToday}
                      </span>
                    </div>
                    <div className="flex items-center justify-between" data-id="element-5770">
                      <p className="text-sm font-bold text-white/50" data-id="element-5771">
                        Pedidos Mesero
                      </p>
                      <span className="text-xs font-bold text-blue-400 bg-blue-500/10 px-2 py-0.5 rounded-md" data-id="element-5772">
                        {metrics.waiterPercent}% atención
                      </span>
                    </div>
                  </div>

                  <div className="bg-[#141414] rounded-2xl p-4 border-l-4 border-y border-r border-white/5 border-l-amber-500 shadow-lg" data-id="element-5773">
                    <div className="flex items-center justify-between mb-2" data-id="element-5774">
                      <div className="w-8 h-8 rounded-full bg-amber-500/20 flex items-center justify-center" data-id="element-5775">
                        <Armchair className="w-4 h-4 text-amber-400" data-id="element-5776" />
                      </div>
                      <span className="text-2xl font-black tabular-nums" data-id="element-5777">
                        {metrics.activeTables}
                      </span>
                    </div>
                    <div className="flex items-center justify-between" data-id="element-5778">
                      <p className="text-sm font-bold text-white/50" data-id="element-5779">
                        Mesas Activas
                      </p>
                      <span className="text-xs font-bold text-white/40" data-id="element-5780">
                        de {metrics.totalTables} totales
                      </span>
                    </div>
                  </div>

                  <div className="bg-[#141414] rounded-2xl p-4 border-l-4 border-y border-r border-white/5 border-l-purple-500 shadow-lg" data-id="element-5781">
                    <div className="flex items-center justify-between mb-2" data-id="element-5782">
                      <div className="w-8 h-8 rounded-full bg-purple-500/20 flex items-center justify-center" data-id="element-5783">
                        <Receipt className="w-4 h-4 text-purple-400" data-id="element-5784" />
                      </div>
                      <span className="text-2xl font-black tabular-nums text-[#F4D35E]" data-id="element-5785">
                        {formatCurrency(metrics.averageTicketPerTable)}
                      </span>
                    </div>
                    <div className="flex items-center justify-between" data-id="element-5786">
                      <p className="text-sm font-bold text-white/50" data-id="element-5787">
                        Ticket Promedio
                      </p>
                      <span className="text-xs font-bold text-white/40" data-id="element-5788">
                        por mesa
                      </span>
                    </div>
                  </div>
                </>}
            </div>

            {/* Tables Grid */}
            {tablesLoading ? <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4" data-id="element-5789">
                {[1, 2, 3, 4, 5, 6, 7, 8, 9, 10].map(i => <div key={i} className="h-40 rounded-2xl bg-[#141414] animate-pulse border border-white/5" data-id="element-5790" />)}
              </div> : tables.length === 0 ? <div className="py-24 flex flex-col items-center justify-center text-white/30 bg-[#141414] rounded-2xl border border-white/5 border-dashed" data-id="element-5791">
                <Armchair className="w-16 h-16 text-white/10 mb-4" data-id="element-5792" />
                <h3 className="text-xl font-black text-white/40 mb-1" data-id="element-5793">
                  No hay mesas configuradas
                </h3>
                <p className="text-sm" data-id="element-5794">
                  Las mesas deben ser creadas desde la configuración del
                  restaurante.
                </p>
              </div> : <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4" data-id="element-5795">
                {tables.map(table => {
            const SourceIcon = getSourceInfo(table.order_channel).icon;
            const sourceColor = getSourceInfo(table.order_channel).color;
            const sourceLabel = getSourceInfo(table.order_channel).label;
            const isOccupied = table.status !== 'free';
            return <div key={table.id} onClick={() => setSelectedTable(table)} className={cn('bg-[#141414] rounded-2xl p-4 border border-white/5 flex flex-col cursor-pointer transition-all hover:border-white/20 hover:shadow-lg hover:-translate-y-0.5 relative overflow-hidden', isOccupied && 'border-white/10 bg-white/[0.02]')} data-id="element-5796">
                      {/* Status Indicator Line */}
                      <div className={cn('absolute top-0 left-0 right-0 h-1', table.status === 'free' && 'bg-emerald-500', table.status === 'occupied' && 'bg-amber-500', table.status === 'ordering' && 'bg-blue-500', table.status === 'waiting_payment' && 'bg-purple-500', table.status === 'closed' && 'bg-gray-500')} data-id="element-5797" />

                      <div className="flex justify-between items-start mb-3 mt-1" data-id="element-5798">
                        <div data-id="element-5799">
                          <h3 className="text-xl font-black text-white leading-none mb-1" data-id="element-5800">
                            {table.table_name || table.table_number}
                          </h3>
                          {table.zone && <span className="text-xs font-bold text-white/40 uppercase tracking-wider" data-id="element-5801">
                              {table.zone}
                            </span>}
                        </div>
                        <div className={cn('px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider border', getStatusColor(table.status))} data-id="element-5802">
                          {getStatusLabel(table.status)}
                        </div>
                      </div>

                      <div className="flex items-center gap-3 text-xs text-white/40 font-medium mb-auto" data-id="element-5803">
                        {table.capacity && <span className="flex items-center gap-1" data-id="element-5804">
                            <Users className="w-3.5 h-3.5" data-id="element-5805" />
                            {table.capacity} pax
                          </span>}
                      </div>

                      {/* Order Info (if occupied) */}
                      <div className="mt-4 pt-3 border-t border-white/5" data-id="element-5806">
                        {isOccupied && table.current_order ? <>
                            <div className="flex justify-between items-end mb-2" data-id="element-5807">
                              <span className="text-lg font-black text-[#F4D35E] tabular-nums leading-none" data-id="element-5808">
                                {formatCurrency(table.current_order.total)}
                              </span>
                              {table.time_open_minutes !== null && <span className="flex items-center gap-1 text-xs font-bold text-white/50" data-id="element-5809">
                                  <Clock className="w-3.5 h-3.5" data-id="element-5810" />
                                  {table.time_open_minutes}m
                                </span>}
                            </div>
                            <div className="flex items-center justify-between" data-id="element-5811">
                              <span className="text-xs text-white/40 font-medium" data-id="element-5812">
                                {table.item_count} items
                              </span>
                              <div className="flex items-center gap-1.5" data-id="element-5813">
                                {(table as any).waiter_name && <span className="text-[10px] text-white/40 truncate max-w-[60px]" data-id="element-5814">
                                    {(table as any).waiter_name}
                                  </span>}
                                <span className={cn('flex items-center gap-1 px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider', sourceColor)} data-id="element-5815">
                                  <SourceIcon className="w-3 h-3" data-id="element-5816" />
                                  {sourceLabel}
                                </span>
                              </div>
                            </div>
                          </> : <div className="flex items-center justify-center h-10 text-xs font-bold text-white/20 uppercase tracking-wider" data-id="element-5817">
                            Mesa disponible
                          </div>}
                      </div>
                    </div>;
          })}
              </div>}
          </div>}

        {/* Table Detail Modal */}
        <div className={usesRanchoStyleTables ? 'text-[#0B0807]' : 'text-white'} data-id="element-5818">
          <Modal isOpen={!!selectedTable} onClose={() => {
          setSelectedTable(null);
          setRanchoMenuMode('main');
        }} title={selectedTable ? `Mesa ${selectedTable.table_name || selectedTable.table_number}` : ''} data-id="element-5819">
            {selectedTable && usesRanchoStyleTables ? <RanchoActionMenu table={selectedTable} menuMode={ranchoMenuMode} setMenuMode={setRanchoMenuMode} onTomarOrden={() => {
            const tn = String(selectedTable.table_number);
            setSelectedTable(null);
            setRanchoMenuMode('main');
            navigate(`/admin/${restaurant.slug}/waiter?mesa=${encodeURIComponent(tn)}`);
          }} onAgregarProductos={() => setShowAddItemsModal(true)} onModificarProductos={() => setShowModifyModal(true)} onCobrarFacturar={() => setShowPayModal(true)} onImprimir={handleImprimirMesa} isPrinting={printingMesa} showImprimirButton={isRanchoSanFrancisco} data-id="element-5820" /> : selectedTable ? <div className="space-y-6 text-gray-900" data-id="element-5821">
                {/* Table Meta */}
                <div className="flex flex-wrap gap-2" data-id="element-5822">
                  <Badge className={cn('border-0', selectedTable.status === 'free' && 'bg-emerald-100 text-emerald-800', selectedTable.status === 'occupied' && 'bg-amber-100 text-amber-800', selectedTable.status === 'ordering' && 'bg-blue-100 text-blue-800', selectedTable.status === 'waiting_payment' && 'bg-purple-100 text-purple-800', selectedTable.status === 'closed' && 'bg-gray-100 text-gray-800')} data-id="element-5823">
                    {getStatusLabel(selectedTable.status)}
                  </Badge>
                  {selectedTable.zone && <Badge variant="secondary" data-id="element-5824">{selectedTable.zone}</Badge>}
                  {selectedTable.capacity && <Badge variant="secondary" className="flex items-center gap-1" data-id="element-5825">
                      <Users className="w-3 h-3" data-id="element-5826" />
                      {selectedTable.capacity} pax
                    </Badge>}
                </div>

                {selectedTable.current_order ? <>
                    {/* Order Summary */}
                    <div className="bg-gray-50 rounded-xl p-4 border border-gray-100" data-id="element-5827">
                      <div className="grid grid-cols-2 gap-4" data-id="element-5828">
                        <div data-id="element-5829">
                          <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1" data-id="element-5830">
                            Orden
                          </p>
                          <p className="font-bold text-gray-900 flex items-center gap-1.5" data-id="element-5831">
                            <Hash className="w-4 h-4 text-gray-400" data-id="element-5832" />
                            {selectedTable.current_order.order_number}
                          </p>
                        </div>
                        <div data-id="element-5833">
                          <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1" data-id="element-5834">
                            Cliente
                          </p>
                          <p className="font-bold text-gray-900 flex items-center gap-1.5 truncate" data-id="element-5835">
                            <User className="w-4 h-4 text-gray-400" data-id="element-5836" />
                            {selectedTable.current_order.customer_name || 'Sin nombre'}
                          </p>
                        </div>
                        <div data-id="element-5837">
                          <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1" data-id="element-5838">
                            Origen
                          </p>
                          <p className="font-bold text-gray-900 flex items-center gap-1.5" data-id="element-5839">
                            {createElement(getSourceInfo(selectedTable.order_channel).icon, {
                        className: 'w-4 h-4 text-gray-400'
                      })}
                            {getSourceInfo(selectedTable.order_channel).label}
                            {(selectedTable as any).waiter_name && <span className="text-gray-500 font-normal text-sm ml-1 truncate max-w-[100px]" data-id="element-5840">
                                ({(selectedTable as any).waiter_name})
                              </span>}
                          </p>
                        </div>
                        <div data-id="element-5841">
                          <p className="text-xs font-bold text-gray-500 uppercase tracking-wider mb-1" data-id="element-5842">
                            Tiempo
                          </p>
                          <p className="font-bold text-gray-900 flex items-center gap-1.5" data-id="element-5843">
                            <Clock className="w-4 h-4 text-gray-400" data-id="element-5844" />
                            {selectedTable.time_open_minutes} min
                          </p>
                        </div>
                      </div>
                    </div>

                    {/* Items List */}
                    <div data-id="element-5845">
                      <h4 className="text-sm font-bold text-gray-900 mb-3 flex items-center gap-2" data-id="element-5846">
                        <ShoppingBag className="w-4 h-4 text-gray-400" data-id="element-5847" />
                        Items del Pedido ({selectedTable.item_count})
                      </h4>
                      <div className="space-y-2 max-h-[40vh] overflow-y-auto pr-2" data-id="element-5848">
                        {itemsLoading ? <div className="flex justify-center py-4" data-id="element-5849">
                            <Loader2 className="w-6 h-6 text-gray-300 animate-spin" data-id="element-5850" />
                          </div> : orderItems.length > 0 ? orderItems.map(item => <div key={item.order_item_id} className="flex justify-between items-start py-3 border-b border-gray-100 last:border-0" data-id="element-5851">
                              <div className="flex gap-3" data-id="element-5852">
                                <span className="font-bold text-gray-900 bg-gray-100 px-2 py-0.5 rounded text-sm h-fit" data-id="element-5853">
                                  {item.quantity}x
                                </span>
                                <div data-id="element-5854">
                                  <p className="font-bold text-gray-900 text-sm" data-id="element-5855">
                                    {item.item_name}
                                  </p>
                                  <div className="flex items-center gap-2 mt-1.5" data-id="element-5856">
                                    {item.station_name && <span className="inline-flex items-center gap-1 text-[10px] font-bold uppercase tracking-wider text-gray-500 bg-gray-100 px-1.5 py-0.5 rounded" data-id="element-5857">
                                        <ChefHat className="w-3 h-3" data-id="element-5858" />
                                        {item.station_name}
                                      </span>}
                                    <span className={cn('inline-flex items-center px-1.5 py-0.5 rounded text-[10px] font-bold uppercase tracking-wider', getItemStatusBadge(item.item_status))} data-id="element-5859">
                                      {getItemStatusLabel(item.item_status)}
                                    </span>
                                  </div>
                                  {item.notes && <p className="text-xs text-gray-500 mt-1.5 italic" data-id="element-5860">
                                      "{item.notes}"
                                    </p>}
                                </div>
                              </div>
                              <span className="font-bold text-gray-900 text-sm tabular-nums shrink-0" data-id="element-5861">
                                {formatCurrency(item.line_total)}
                              </span>
                            </div>) : <p className="text-sm text-gray-500 italic py-2" data-id="element-5862">
                            No se pudieron cargar los items.
                          </p>}
                      </div>
                    </div>

                    {/* Total & Payment */}
                    <div className="pt-4 border-t border-gray-200" data-id="element-5863">
                      <div className="flex justify-between items-center mb-6" data-id="element-5864">
                        <span className="font-bold text-gray-900" data-id="element-5865">
                          Total a pagar
                        </span>
                        <span className="text-2xl font-black text-brand-amber tabular-nums" data-id="element-5866">
                          {formatCurrency(selectedTable.current_order.total)}
                        </span>
                      </div>

                      {/* Cabaña + Rancho — Adicionar productos a la mesa */}
                      {(isCabanaParrillera || isRanchoSanFrancisco) && selectedTable.current_order.status !== 'delivered' && selectedTable.current_order.status !== 'cancelled' && <Button fullWidth size="lg" variant="outline" onClick={() => setShowAddItemsModal(true)} className="mb-3 border-2 border-amber-400 text-amber-700 hover:bg-amber-50 font-bold flex items-center justify-center gap-2" data-id="element-5867">
                            <Plus className="w-5 h-5" data-id="element-5868" />
                            Adicionar productos a la mesa
                          </Button>}

                      {/* Cabaña + Rancho — Modificar / Eliminar productos */}
                      {(isCabanaParrillera || isRanchoSanFrancisco) && selectedTable.current_order.status !== 'delivered' && selectedTable.current_order.status !== 'cancelled' && <Button fullWidth size="lg" variant="outline" onClick={() => setShowModifyModal(true)} className="mb-3 border-2 border-blue-400 text-blue-700 hover:bg-blue-50 font-bold flex items-center justify-center gap-2" data-id="element-5869">
                            <Pencil className="w-5 h-5" data-id="element-5870" />
                            Modificar / Eliminar productos
                          </Button>}

                      {/* Primary: Cobrar y Facturar */}
                      <Button fullWidth size="lg" onClick={() => setShowPayModal(true)} className="bg-amber-500 hover:bg-amber-600 text-white font-bold shadow-lg flex items-center justify-center gap-2" data-id="element-5871">
                        <Receipt className="w-5 h-5" data-id="element-5872" />
                        Cobrar y Facturar
                      </Button>

                      <details className="mt-3" data-id="element-5873">
                        <summary className="text-xs text-gray-400 hover:text-gray-600 cursor-pointer text-center" data-id="element-5874">
                          Cerrar sin facturar (legacy)
                        </summary>
                        <div className="mt-3 space-y-3" data-id="element-5875">
                          <p className="text-xs font-bold text-gray-500" data-id="element-5876">
                            Método de pago
                          </p>
                          <div className="grid grid-cols-3 gap-2" data-id="element-5877">
                            <label className={cn('flex flex-col items-center justify-center p-3 rounded-xl border-2 cursor-pointer transition-all', selectedPayment === 'cash' ? 'border-brand-amber bg-amber-50 text-brand-amber' : 'border-gray-100 hover:border-gray-200 text-gray-600')} data-id="element-5878">
                              <input type="radio" name="payment" value="cash" checked={selectedPayment === 'cash'} onChange={() => setSelectedPayment('cash')} className="sr-only" data-id="element-5879" />
                              <Banknote className="w-5 h-5 mb-1" data-id="element-5880" />
                              <span className="text-xs font-bold" data-id="element-5881">
                                Efectivo
                              </span>
                            </label>
                            <label className={cn('flex flex-col items-center justify-center p-3 rounded-xl border-2 cursor-pointer transition-all', selectedPayment === 'card' ? 'border-brand-amber bg-amber-50 text-brand-amber' : 'border-gray-100 hover:border-gray-200 text-gray-600')} data-id="element-5882">
                              <input type="radio" name="payment" value="card" checked={selectedPayment === 'card'} onChange={() => setSelectedPayment('card')} className="sr-only" data-id="element-5883" />
                              <CreditCard className="w-5 h-5 mb-1" data-id="element-5884" />
                              <span className="text-xs font-bold" data-id="element-5885">Tarjeta</span>
                            </label>
                            <label className={cn('flex flex-col items-center justify-center p-3 rounded-xl border-2 cursor-pointer transition-all', selectedPayment === 'sinpe' ? 'border-brand-amber bg-amber-50 text-brand-amber' : 'border-gray-100 hover:border-gray-200 text-gray-600')} data-id="element-5886">
                              <input type="radio" name="payment" value="sinpe" checked={selectedPayment === 'sinpe'} onChange={() => setSelectedPayment('sinpe')} className="sr-only" data-id="element-5887" />
                              <Smartphone className="w-5 h-5 mb-1" data-id="element-5888" />
                              <span className="text-xs font-bold" data-id="element-5889">SINPE</span>
                            </label>
                          </div>
                          <Button fullWidth size="md" onClick={handleCloseAccount} disabled={isClosing} className="bg-red-600 hover:bg-red-500 text-white font-bold" data-id="element-5890">
                            {isClosing ? <span className="flex items-center justify-center gap-2" data-id="element-5891">
                                <Loader2 className="w-5 h-5 animate-spin" data-id="element-5892" />
                                Cerrando cuenta...
                              </span> : 'Cerrar Cuenta'}
                          </Button>
                        </div>
                      </details>
                    </div>
                  </> : <div className="py-12 flex flex-col items-center justify-center text-gray-400 bg-gray-50 rounded-xl border border-gray-100 border-dashed" data-id="element-5893">
                    <Armchair className="w-12 h-12 text-gray-300 mb-3" data-id="element-5894" />
                    <p className="font-medium text-gray-600" data-id="element-5895">Mesa disponible</p>
                    <p className="text-sm" data-id="element-5896">
                      No hay ninguna orden activa en esta mesa.
                    </p>
                  </div>}
              </div> : null}
          </Modal>
        </div>

        {/* Pay Modal — split bill + auto invoice */}
        <PayTableModal isOpen={showPayModal} onClose={() => setShowPayModal(false)} table={selectedTable} restaurantId={restaurant?.id} createdBy={tenantCtx?.userId || null} onSuccess={allPaid => {
        addToast('success', allPaid ? 'Cuenta cerrada y factura emitida' : 'Pago parcial registrado — saldo pendiente');
        refetchTables();
        if (allPaid) {
          setShowPayModal(false);
          setSelectedTable(null);
          setRanchoMenuMode('main');
        }
      }} data-id="element-5897" />

        {/* Cabaña + Rancho + NatuCafé — Adicionar productos a la mesa */}
        {(isCabanaParrillera || usesRanchoStyleTables) && <CabanaAddItemsModal isOpen={showAddItemsModal} onClose={() => setShowAddItemsModal(false)} orderId={selectedTable?.current_order?.id || null} restaurantId={restaurant?.id} onAdded={() => {
        addToast('success', 'Productos adicionados a la mesa');
        refetchTables();
        refetchItems?.();
      }} data-id="element-5898" />}

        {/* Cabaña + Rancho + NatuCafé — Modificar / Eliminar productos / Cancelar orden */}
        {(isCabanaParrillera || usesRanchoStyleTables) && <CabanaModifyOrderModal isOpen={showModifyModal} onClose={() => setShowModifyModal(false)} orderId={selectedTable?.current_order?.id || null} requireVoidAuth={isNatuCafe} restaurantId={restaurant?.id} onChanged={() => {
        addToast('success', 'Orden actualizada');
        refetchTables();
        refetchItems?.();
        refetchAdminOrders?.();
      }} onCancelOrder={async orderId => {
        const res = await cancelOrder(orderId);
        if (res.success) {
          addToast('success', '❌ Pedido cancelado');
          setShowModifyModal(false);
          setSelectedTable(null);
          setRanchoMenuMode('main');
          refetchTables();
        } else if (res.error) {
          addToast('error', res.error);
        }
        return res;
      }} data-id="element-5899" />}

        {/* Toast Notifications */}
        {toasts.length > 0 && <div className="fixed bottom-6 right-6 z-50 flex flex-col gap-2 pointer-events-none" data-id="element-5900">
            {toasts.map(toast => <div key={toast.id} className={cn('pointer-events-auto flex items-center gap-3 px-5 py-3 rounded-xl shadow-2xl border text-sm font-bold min-w-[280px] max-w-[400px] anim-slide-up', toast.type === 'success' ? 'bg-emerald-600 border-emerald-500 text-white' : 'bg-red-600 border-red-500 text-white')} data-id="element-5901">
                <span className="flex-1" data-id="element-5902">{toast.message}</span>
                <button onClick={() => removeToast(toast.id)} className="p-0.5 hover:bg-white/20 rounded transition-colors shrink-0" data-id="element-5903">
                  <X className="w-4 h-4" data-id="element-5904" />
                </button>
              </div>)}
          </div>}
      </AdminLayout>
    </ErrorBoundary>;
}
// ─── Rancho San Francisco — Action menu inside the mesa modal ───────
// Diseñado para señores de 70 años: una sola acción visible a la vez,
// botones grandes, copy en español claro, sub-menú para "Editar".
interface RanchoActionMenuProps {
  table: TableWithOrder;
  menuMode: 'main' | 'edit';
  setMenuMode: (m: 'main' | 'edit') => void;
  onTomarOrden: () => void;
  onAgregarProductos: () => void;
  onModificarProductos: () => void;
  onCobrarFacturar: () => void;
  // Rancho-only: opcional. Cuando showImprimirButton=true (Rancho id 28)
  // y la mesa está ocupada, mostramos un botón "Imprimir comanda" que
  // llama a onImprimir. NatuCafé y otros no pasan estos props, así que
  // el botón no aparece para ellos.
  onImprimir?: () => void;
  isPrinting?: boolean;
  showImprimirButton?: boolean;
}
function RanchoActionMenu({
  table,
  menuMode,
  setMenuMode,
  onTomarOrden,
  onAgregarProductos,
  onModificarProductos,
  onCobrarFacturar,
  onImprimir,
  isPrinting,
  showImprimirButton
}: RanchoActionMenuProps) {
  const isOccupied = table.status !== 'free' && !!table.current_order;
  // ── Mesa libre: una sola opción, Tomar orden ──────────────────────
  if (!isOccupied) {
    return <div className="space-y-5 text-[#0B0807]" data-id="element-5905">
        <div className="flex items-center gap-3 px-4 py-3 rounded-2xl bg-emerald-50 border border-emerald-100" data-id="element-5906">
          <Armchair className="w-6 h-6 text-emerald-600" data-id="element-5907" />
          <div data-id="element-5908">
            <p className="font-bold text-emerald-800 leading-tight" data-id="element-5909">
              Mesa libre
            </p>
            <p className="text-sm text-emerald-700" data-id="element-5910">
              Esta mesa no tiene una orden abierta.
            </p>
          </div>
        </div>
        <button onClick={onTomarOrden} className="w-full flex items-center justify-center gap-3 py-5 rounded-2xl bg-[#0B0807] hover:bg-black text-white font-black text-xl shadow-lg transition-all hover:-translate-y-0.5" data-id="element-5911">
          <ShoppingBag className="w-7 h-7" data-id="element-5912" />
          Tomar orden
        </button>
      </div>;
  }
  // ── Mesa ocupada: menú principal o sub-menú de Editar ─────────────
  return <div className="space-y-5 text-[#0B0807]" data-id="element-5913">
      {/* Resumen mínimo de la mesa ocupada */}
      <div className="flex items-center justify-between gap-3 px-4 py-3 rounded-2xl bg-amber-50 border border-amber-200" data-id="element-5914">
        <div className="flex items-center gap-3" data-id="element-5915">
          <Receipt className="w-6 h-6 text-amber-700" data-id="element-5916" />
          <div data-id="element-5917">
            <p className="font-bold text-amber-900 leading-tight" data-id="element-5918">
              Mesa ocupada
            </p>
            {table.current_order && <p className="text-sm text-amber-800 tabular-nums" data-id="element-5919">
                Total:{' '}
                <strong data-id="element-5920">{formatCurrency(table.current_order.total)}</strong>
                {typeof table.item_count === 'number' && <>
                    {' · '}
                    {table.item_count} items
                  </>}
              </p>}
          </div>
        </div>
      </div>

      {menuMode === 'main' ? <div className="space-y-3" data-id="element-5921">
          <button onClick={() => setMenuMode('edit')} className="w-full flex items-center gap-4 px-5 py-5 rounded-2xl bg-white border-2 border-blue-300 hover:border-blue-500 hover:bg-blue-50 transition-all text-left shadow-sm" data-id="element-5922">
            <div className="w-12 h-12 rounded-xl bg-blue-100 flex items-center justify-center shrink-0" data-id="element-5923">
              <Pencil className="w-6 h-6 text-blue-700" data-id="element-5924" />
            </div>
            <div className="flex-1" data-id="element-5925">
              <p className="font-black text-lg text-[#0B0807] leading-tight" data-id="element-5926">
                Editar / Modificar orden
              </p>
              <p className="text-sm text-gray-600" data-id="element-5927">
                Agregar o quitar productos de la mesa
              </p>
            </div>
          </button>

          {/* Rancho San Francisco — el botón "Imprimir comanda" fue movido
                                    a la segunda pantalla del cobro (PaidStep en PayTableModal),
                                    justo después de confirmar el pago. Se quitó de aquí. */}

          <button onClick={onCobrarFacturar} className="w-full flex items-center gap-4 px-5 py-5 rounded-2xl bg-amber-500 hover:bg-amber-600 transition-all text-left shadow-lg" data-id="element-5928">
            <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center shrink-0" data-id="element-5929">
              <Receipt className="w-6 h-6 text-white" data-id="element-5930" />
            </div>
            <div className="flex-1" data-id="element-5931">
              <p className="font-black text-lg text-white leading-tight" data-id="element-5932">
                Cobrar y Facturar
              </p>
              <p className="text-sm text-white/90" data-id="element-5933">
                Recibir el pago y emitir la factura
              </p>
            </div>
          </button>
        </div> : <div className="space-y-3" data-id="element-5934">
          <button onClick={() => setMenuMode('main')} className="text-sm font-bold text-gray-500 hover:text-gray-800 flex items-center gap-1.5" data-id="element-5935">
            ← Volver
          </button>

          <button onClick={onAgregarProductos} className="w-full flex items-center gap-4 px-5 py-5 rounded-2xl bg-white border-2 border-emerald-300 hover:border-emerald-500 hover:bg-emerald-50 transition-all text-left shadow-sm" data-id="element-5936">
            <div className="w-12 h-12 rounded-xl bg-emerald-100 flex items-center justify-center shrink-0" data-id="element-5937">
              <Plus className="w-6 h-6 text-emerald-700" data-id="element-5938" />
            </div>
            <div className="flex-1" data-id="element-5939">
              <p className="font-black text-lg text-[#0B0807] leading-tight" data-id="element-5940">
                Agregar productos
              </p>
              <p className="text-sm text-gray-600" data-id="element-5941">
                Añadir más cosas a la cuenta de la mesa
              </p>
            </div>
          </button>

          <button onClick={onModificarProductos} className="w-full flex items-center gap-4 px-5 py-5 rounded-2xl bg-white border-2 border-red-200 hover:border-red-400 hover:bg-red-50 transition-all text-left shadow-sm" data-id="element-5942">
            <div className="w-12 h-12 rounded-xl bg-red-100 flex items-center justify-center shrink-0" data-id="element-5943">
              <X className="w-6 h-6 text-red-700" data-id="element-5944" />
            </div>
            <div className="flex-1" data-id="element-5945">
              <p className="font-black text-lg text-[#0B0807] leading-tight" data-id="element-5946">
                Quitar productos / cancelar
              </p>
              <p className="text-sm text-gray-600" data-id="element-5947">
                Eliminar productos o cancelar la orden completa
              </p>
            </div>
          </button>
        </div>}
    </div>;
}